import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Mail, Paperclip, X, Info, MessageCircle, Eye, Copy, ChevronDown, ChevronUp } from 'lucide-react';
import RichTextEditor from '@/components/RichTextEditor';
import { confirmAsync } from '@/lib/confirm';

export default function SendTemplates({ user }) {
  // Email Templates State
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [editingEmailTemplate, setEditingEmailTemplate] = useState(null);
  const [emailName, setEmailName] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [attachments, setAttachments] = useState([]);

  // WhatsApp Templates State
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [editingWhatsappTemplate, setEditingWhatsappTemplate] = useState(null);
  const [whatsappName, setWhatsappName] = useState('');
  const [whatsappMessage, setWhatsappMessage] = useState('');

  // Preview State
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [previewType, setPreviewType] = useState('email'); // 'email' or 'whatsapp'

  // Info Dialog
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  useEffect(() => {
    fetchEmailTemplates();
    fetchWhatsAppTemplates();
  }, []);

  const fetchEmailTemplates = async () => {
    try {
      const response = await axiosInstance.get('/email-templates');
      setEmailTemplates(response.data);
    } catch (error) {
      toast.error('Error al cargar plantillas de email');
    }
  };

  const fetchWhatsAppTemplates = async () => {
    try {
      const response = await axiosInstance.get('/whatsapp-templates');
      setWhatsappTemplates(response.data);
    } catch (error) {
      toast.error('Error al cargar plantillas de WhatsApp');
    }
  };

  // Email Template Functions
  const handleOpenEmailDialog = (template = null) => {
    if (template) {
      setEditingEmailTemplate(template);
      setEmailName(template.name);
      setEmailSubject(template.subject);
      setEmailBody(template.body);
      setAttachments(template.attachments || []);
    } else {
      resetEmailForm();
    }
    setEmailDialogOpen(true);
  };

  const resetEmailForm = () => {
    setEditingEmailTemplate(null);
    setEmailName('');
    setEmailSubject('');
    setEmailBody('');
    setAttachments([]);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    if (attachments.length + files.length > 4) {
      toast.error('Máximo 4 archivos adjuntos');
      return;
    }
    
    const maxSize = 4 * 1024 * 1024;
    let currentSize = attachments.reduce((sum, att) => sum + att.size, 0);
    let newSize = 0;
    
    for (const file of files) {
      newSize += file.size;
    }
    
    if (currentSize + newSize > maxSize) {
      toast.error('El tamaño total de archivos excede 4MB');
      return;
    }
    
    const newAttachments = [];
    for (const file of files) {
      const reader = new FileReader();
      await new Promise((resolve) => {
        reader.onload = (e) => {
          newAttachments.push({
            filename: file.name,
            content: e.target.result.split(',')[1],
            size: file.size
          });
          resolve();
        };
        reader.readAsDataURL(file);
      });
    }
    
    setAttachments([...attachments, ...newAttachments]);
  };

  const handleRemoveAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleSaveEmail = async () => {
    if (!emailName.trim()) {
      toast.error('Ingresa un nombre para la plantilla');
      return;
    }
    
    if (!emailSubject.trim()) {
      toast.error('Ingresa un asunto');
      return;
    }
    
    if (!emailBody.trim()) {
      toast.error('Ingresa el cuerpo del mensaje');
      return;
    }

    try {
      const payload = {
        name: emailName,
        subject: emailSubject,
        body: emailBody,
        attachments
      };

      if (editingEmailTemplate) {
        await axiosInstance.put(`/email-templates/${editingEmailTemplate.id}`, payload);
        toast.success('Plantilla actualizada correctamente');
      } else {
        await axiosInstance.post('/email-templates', payload);
        toast.success('Plantilla creada correctamente');
      }

      setEmailDialogOpen(false);
      resetEmailForm();
      fetchEmailTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar plantilla');
    }
  };

  const handleDeleteEmail = async (templateId) => {
    if (!(await confirmAsync('¿Estás seguro de que deseas eliminar esta plantilla?'))) {
      return;
    }

    try {
      await axiosInstance.delete(`/email-templates/${templateId}`);
      toast.success('Plantilla eliminada correctamente');
      fetchEmailTemplates();
    } catch (error) {
      toast.error('Error al eliminar plantilla');
    }
  };

  // WhatsApp Template Functions
  const handleOpenWhatsAppDialog = (template = null) => {
    if (template) {
      setEditingWhatsappTemplate(template);
      setWhatsappName(template.name);
      setWhatsappMessage(template.message);
    } else {
      resetWhatsAppForm();
    }
    setWhatsappDialogOpen(true);
  };

  const resetWhatsAppForm = () => {
    setEditingWhatsappTemplate(null);
    setWhatsappName('');
    setWhatsappMessage('');
  };

  const handleSaveWhatsApp = async () => {
    if (!whatsappName.trim()) {
      toast.error('Ingresa un nombre para la plantilla');
      return;
    }
    
    if (!whatsappMessage.trim()) {
      toast.error('Ingresa el mensaje');
      return;
    }

    if (whatsappMessage.length > 4096) {
      toast.error('El mensaje no puede superar 4096 caracteres');
      return;
    }

    try {
      const payload = {
        name: whatsappName,
        message: whatsappMessage
      };

      if (editingWhatsappTemplate) {
        await axiosInstance.put(`/whatsapp-templates/${editingWhatsappTemplate.id}`, payload);
        toast.success('Plantilla actualizada correctamente');
      } else {
        await axiosInstance.post('/whatsapp-templates', payload);
        toast.success('Plantilla creada correctamente');
      }

      setWhatsappDialogOpen(false);
      resetWhatsAppForm();
      fetchWhatsAppTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar plantilla');
    }
  };

  const handleDeleteWhatsApp = async (templateId) => {
    if (!(await confirmAsync('¿Estás seguro de que deseas eliminar esta plantilla?'))) {
      return;
    }

    try {
      await axiosInstance.delete(`/whatsapp-templates/${templateId}`);
      toast.success('Plantilla eliminada correctamente');
      fetchWhatsAppTemplates();
    } catch (error) {
      toast.error('Error al eliminar plantilla');
    }
  };

  // Preview Functions
  const handlePreview = (template, type) => {
    const sampleData = {
      nombre_cliente: 'Juan Pérez',
      email_cliente: 'juan@ejemplo.com',
      telefono_cliente: '+34 612 345 678',
      empresa_cliente: 'Restaurante El Patio',
      nombre_empresa: 'Digital Restaurant SL',
      numero_presupuesto: 'P-2024-001',
      numero_factura: 'F-2024-001',
      fecha: new Date().toLocaleDateString('es-ES'),
      total: '1.250,00 €',
      usuario: user?.name || 'Usuario'
    };

    let content = type === 'email' ? template.body : template.message;
    
    // Replace variables
    Object.keys(sampleData).forEach(key => {
      const regex = new RegExp(`{${key}}`, 'g');
      content = content.replace(regex, sampleData[key]);
    });

    setPreviewContent(content);
    setPreviewType(type);
    setPreviewDialogOpen(true);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const [variableFilter, setVariableFilter] = useState('all'); // 'all', 'cliente', 'factura', 'empresa'
  const [variablesPanelOpen, setVariablesPanelOpen] = useState(false);

  // Función para copiar variable al portapapeles
  const copyVariable = (variable) => {
    navigator.clipboard.writeText(variable);
    toast.success(`Variable ${variable} copiada`);
  };

  const clientVariables = [
    { var: '{nombre_cliente}', desc: 'Nombre del cliente' },
    { var: '{email}', desc: 'Email del cliente' },
    { var: '{telefono}', desc: 'Teléfono del cliente' },
    { var: '{empresa}', desc: 'Empresa del cliente' },
    { var: '{nombre_comercial}', desc: 'Nombre comercial del cliente' },
    { var: '{nombre_fiscal}', desc: 'Nombre fiscal del cliente' },
    { var: '{direccion}', desc: 'Dirección del cliente' },
    { var: '{ciudad}', desc: 'Ciudad del cliente' },
    { var: '{codigo_postal}', desc: 'Código postal del cliente' },
    { var: '{provincia}', desc: 'Provincia del cliente' },
  ];

  const invoiceVariables = [
    { var: '{numero_factura}', desc: 'Número de factura' },
    { var: '{numero_presupuesto}', desc: 'Número de presupuesto origen' },
    { var: '{importe}', desc: 'Importe total de la factura' },
    { var: '{subtotal}', desc: 'Subtotal (sin IVA)' },
    { var: '{iva}', desc: 'Importe del IVA' },
    { var: '{fecha_emision}', desc: 'Fecha de emisión' },
    { var: '{fecha_vencimiento}', desc: 'Fecha de vencimiento' },
    { var: '{forma_pago}', desc: 'Forma de pago' },
    { var: '{link_portal}', desc: 'Link al portal del cliente' },
    { var: '{periodo_mes}', desc: 'Período (mes y año)' },
    { var: '{periodo_ano}', desc: 'Año del período' },
  ];

  const companyVariables = [
    { var: '{telefono_comercial}', desc: 'Teléfono comercial de tu empresa' },
    { var: '{telefono_ventas}', desc: 'Teléfono de ventas de tu empresa' },
    { var: '{whatsapp_comercial}', desc: 'WhatsApp comercial de tu empresa' },
    { var: '{whatsapp_ventas}', desc: 'WhatsApp de ventas de tu empresa' },
    { var: '{email_comercial}', desc: 'Email comercial de tu empresa' },
    { var: '{email_administracion}', desc: 'Email de administración de tu empresa' },
    { var: '{email_general}', desc: 'Email general de tu empresa' },
    { var: '{email_ventas}', desc: 'Email de ventas de tu empresa' },
    { var: '{pagina_web}', desc: 'Página web de tu empresa' },
    { var: '{linkedin}', desc: 'LinkedIn de tu empresa' },
    { var: '{facebook}', desc: 'Facebook de tu empresa' },
    { var: '{instagram}', desc: 'Instagram de tu empresa' },
    { var: '{tiktok}', desc: 'TikTok de tu empresa' },
    { var: '{twitter_x}', desc: 'X (Twitter) de tu empresa' },
    { var: '{google_my_business}', desc: 'Google My Business de tu empresa' },
  ];

  const getFilteredVariables = () => {
    if (variableFilter === 'cliente') return clientVariables;
    if (variableFilter === 'factura') return invoiceVariables;
    if (variableFilter === 'empresa') return companyVariables;
    return [...clientVariables, ...invoiceVariables, ...companyVariables];
  };

  return (
    <Layout user={user}>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-[#212B35] mb-2">Plantillas de Envío</h1>
            <p className="text-[#212B35]/70">Gestiona plantillas reutilizables para email y WhatsApp</p>
          </div>
          <Button
            variant="outline"
            onClick={() => setInfoDialogOpen(true)}
            className="border-[#9C0F17] text-[#9C0F17] hover:bg-[#9C0F17]/10"
          >
            <Info className="w-4 h-4 mr-2" />
            Variables Disponibles
          </Button>
        </div>

        {/* Variables Info Dialog */}
        <Dialog open={infoDialogOpen} onOpenChange={setInfoDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Variables Dinámicas Disponibles</DialogTitle>
              <DialogDescription>
                Usa estas variables en tus plantillas. Se reemplazarán automáticamente con datos reales.
              </DialogDescription>
            </DialogHeader>
            
            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-2 mb-4">
              <Button
                variant={variableFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setVariableFilter('all')}
                className={variableFilter === 'all' ? 'bg-[#9C0F17]' : ''}
              >
                Todas
              </Button>
              <Button
                variant={variableFilter === 'cliente' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setVariableFilter('cliente')}
                className={variableFilter === 'cliente' ? 'bg-[#9C0F17]' : ''}
              >
                Cliente
              </Button>
              <Button
                variant={variableFilter === 'factura' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setVariableFilter('factura')}
                className={variableFilter === 'factura' ? 'bg-[#9C0F17]' : ''}
              >
                Factura
              </Button>
              <Button
                variant={variableFilter === 'empresa' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setVariableFilter('empresa')}
                className={variableFilter === 'empresa' ? 'bg-[#9C0F17]' : ''}
              >
                Mi Empresa
              </Button>
            </div>

            <div className="space-y-2">
              {getFilteredVariables().map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <code className="text-sm font-mono bg-gray-200 px-2 py-1 rounded text-[#9C0F17]">
                    {item.var}
                  </code>
                  <span className="text-sm text-gray-600">{item.desc}</span>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>
                {previewType === 'email' ? '📧 Vista Previa Email' : '💬 Vista Previa WhatsApp'}
              </DialogTitle>
              <DialogDescription>
                Vista previa con datos de ejemplo
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-96 overflow-y-auto">
              {previewType === 'email' ? (
                <div 
                  className="p-4 bg-white border rounded-lg"
                  dangerouslySetInnerHTML={{ __html: previewContent }}
                />
              ) : (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="whitespace-pre-wrap font-sans text-sm">
                    {previewContent}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Tabs */}
        <Tabs defaultValue="email" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="email">
              <Mail className="w-4 h-4 mr-2" />
              Email
            </TabsTrigger>
            <TabsTrigger value="whatsapp">
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </TabsTrigger>
          </TabsList>

          {/* EMAIL TAB */}
          <TabsContent value="email">
            <div className="flex justify-end mb-4">
              <Dialog open={emailDialogOpen} onOpenChange={(open) => {
                setEmailDialogOpen(open);
                if (!open) resetEmailForm();
              }}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-[#9C0F17] hover:bg-[#7A0C12] text-white"
                    onClick={() => handleOpenEmailDialog()}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nueva Plantilla Email
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>
                      {editingEmailTemplate ? 'Editar Plantilla Email' : 'Nueva Plantilla de Email'}
                    </DialogTitle>
                    <DialogDescription>
                      Crea plantillas reutilizables con variables dinámicas. Max 4 adjuntos, 4MB total.
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="email-name">Nombre de la Plantilla *</Label>
                      <Input
                        id="email-name"
                        value={emailName}
                        onChange={(e) => setEmailName(e.target.value)}
                        placeholder="Ej: Bienvenida Cliente, Seguimiento Presupuesto..."
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email-subject">Asunto *</Label>
                      <Input
                        id="email-subject"
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        placeholder="Ej: Bienvenido {nombre_cliente} - {nombre_empresa}"
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Puedes usar variables como {'{nombre_cliente}'}, {'{numero_presupuesto}'}, etc.
                      </p>
                    </div>

                    {/* Panel de Variables Disponibles */}
                    <Collapsible open={variablesPanelOpen} onOpenChange={setVariablesPanelOpen}>
                      <CollapsibleTrigger asChild>
                        <Button variant="outline" className="w-full justify-between border-blue-300 bg-blue-50 hover:bg-blue-100">
                          <span className="flex items-center gap-2">
                            <Info className="w-4 h-4 text-blue-600" />
                            <span className="text-blue-700 font-medium">Variables Disponibles</span>
                          </span>
                          {variablesPanelOpen ? (
                            <ChevronUp className="w-4 h-4 text-blue-600" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-blue-600" />
                          )}
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-2">
                        <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
                          {/* Filtros */}
                          <div className="flex flex-wrap gap-2">
                            <Button
                              variant={variableFilter === 'all' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setVariableFilter('all')}
                              className={variableFilter === 'all' ? 'bg-[#9C0F17]' : ''}
                            >
                              Todas
                            </Button>
                            <Button
                              variant={variableFilter === 'cliente' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setVariableFilter('cliente')}
                              className={variableFilter === 'cliente' ? 'bg-[#9C0F17]' : ''}
                            >
                              Cliente
                            </Button>
                            <Button
                              variant={variableFilter === 'factura' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setVariableFilter('factura')}
                              className={variableFilter === 'factura' ? 'bg-[#9C0F17]' : ''}
                            >
                              Factura
                            </Button>
                            <Button
                              variant={variableFilter === 'empresa' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setVariableFilter('empresa')}
                              className={variableFilter === 'empresa' ? 'bg-[#9C0F17]' : ''}
                            >
                              Mi Empresa
                            </Button>
                          </div>
                          {/* Lista de Variables */}
                          <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                            {getFilteredVariables().map((item, index) => (
                              <div 
                                key={index} 
                                className="flex items-center justify-between p-2 bg-white rounded border hover:border-blue-300 cursor-pointer group"
                                onClick={() => copyVariable(item.var)}
                              >
                                <div className="flex-1 min-w-0">
                                  <code className="text-xs font-mono text-[#9C0F17] block truncate">{item.var}</code>
                                  <span className="text-xs text-gray-500 truncate block">{item.desc}</span>
                                </div>
                                <Copy className="w-4 h-4 text-gray-400 group-hover:text-blue-600 ml-2 flex-shrink-0" />
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-gray-500 text-center">
                            💡 Haz clic en una variable para copiarla
                          </p>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>

                    <div>
                      <Label>Cuerpo del Mensaje *</Label>
                      <div className="mt-1 border rounded-lg">
                        <RichTextEditor
                          key={editingEmailTemplate ? editingEmailTemplate.id : 'new'}
                          value={emailBody}
                          onChange={setEmailBody}
                          placeholder="Escribe el mensaje aquí. Puedes usar variables como {nombre_cliente}..."
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Usa el editor para formatear tu mensaje. Las variables se reemplazarán automáticamente.
                      </p>
                    </div>

                    <div>
                      <Label>Archivos Adjuntos (Opcional)</Label>
                      <div className="mt-2 space-y-2">
                        <input
                          type="file"
                          id="file-upload"
                          multiple
                          onChange={handleFileUpload}
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => document.getElementById('file-upload').click()}
                          disabled={attachments.length >= 4}
                          className="w-full"
                        >
                          <Paperclip className="w-4 h-4 mr-2" />
                          Añadir Archivos ({attachments.length}/4)
                        </Button>
                        <p className="text-xs text-gray-500">
                          Máximo 4 archivos, 4MB en total
                        </p>

                        {attachments.length > 0 && (
                          <div className="space-y-2">
                            {attachments.map((att, index) => (
                              <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <div className="flex items-center gap-2">
                                  <Paperclip className="w-4 h-4 text-gray-500" />
                                  <span className="text-sm">{att.filename}</span>
                                  <Badge variant="outline" className="text-xs">
                                    {formatFileSize(att.size)}
                                  </Badge>
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveAttachment(index)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                            <div className="text-xs text-gray-600">
                              Total: {formatFileSize(attachments.reduce((sum, att) => sum + att.size, 0))} / 4MB
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <Button 
                      onClick={handleSaveEmail} 
                      className="w-full bg-[#9C0F17] hover:bg-[#7A0C12]"
                    >
                      {editingEmailTemplate ? 'Actualizar Plantilla' : 'Crear Plantilla'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Email Templates List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {emailTemplates.length === 0 ? (
                <Card className="col-span-full">
                  <CardContent className="py-12 text-center">
                    <Mail className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-600">No hay plantillas de email creadas</p>
                  </CardContent>
                </Card>
              ) : (
                emailTemplates.map((template) => (
                  <Card key={template.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span className="text-lg truncate">{template.name}</span>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handlePreview(template, 'email')}
                            title="Vista previa"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOpenEmailDialog(template)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteEmail(template.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </CardTitle>
                      <CardDescription className="truncate">{template.subject}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-600 line-clamp-3" 
                           dangerouslySetInnerHTML={{ __html: template.body }}
                        />
                      </div>
                      
                      {template.attachments && template.attachments.length > 0 && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Paperclip className="w-4 h-4" />
                          <span>{template.attachments.length} adjunto(s)</span>
                        </div>
                      )}
                      
                      <div className="text-xs text-gray-500">
                        Creada: {new Date(template.created_at).toLocaleDateString('es-ES')}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* WHATSAPP TAB */}
          <TabsContent value="whatsapp">
            <div className="flex justify-end mb-4">
              <Dialog open={whatsappDialogOpen} onOpenChange={(open) => {
                setWhatsappDialogOpen(open);
                if (!open) resetWhatsAppForm();
              }}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => handleOpenWhatsAppDialog()}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nueva Plantilla WhatsApp
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>
                      {editingWhatsappTemplate ? 'Editar Plantilla WhatsApp' : 'Nueva Plantilla de WhatsApp'}
                    </DialogTitle>
                    <DialogDescription>
                      Crea plantillas reutilizables para WhatsApp. Máximo 4096 caracteres.
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="whatsapp-name">Nombre de la Plantilla *</Label>
                      <Input
                        id="whatsapp-name"
                        value={whatsappName}
                        onChange={(e) => setWhatsappName(e.target.value)}
                        placeholder="Ej: Bienvenida, Seguimiento, Confirmación..."
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="whatsapp-message">Mensaje *</Label>
                      <Textarea
                        id="whatsapp-message"
                        value={whatsappMessage}
                        onChange={(e) => setWhatsappMessage(e.target.value)}
                        placeholder="Hola {nombre_cliente}, bienvenido a {nombre_empresa}..."
                        className="mt-1 min-h-[200px]"
                        maxLength={4096}
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>Puedes usar variables como {'{nombre_cliente}'}, {'{telefono_cliente}'}, etc.</span>
                        <span className={whatsappMessage.length > 4000 ? 'text-orange-600 font-semibold' : ''}>
                          {whatsappMessage.length}/4096
                        </span>
                      </div>
                    </div>

                    <Button 
                      onClick={handleSaveWhatsApp} 
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      {editingWhatsappTemplate ? 'Actualizar Plantilla' : 'Crear Plantilla'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* WhatsApp Templates List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {whatsappTemplates.length === 0 ? (
                <Card className="col-span-full">
                  <CardContent className="py-12 text-center">
                    <MessageCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-600">No hay plantillas de WhatsApp creadas</p>
                  </CardContent>
                </Card>
              ) : (
                whatsappTemplates.map((template) => (
                  <Card key={template.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span className="text-lg truncate">{template.name}</span>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handlePreview(template, 'whatsapp')}
                            title="Vista previa"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOpenWhatsAppDialog(template)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteWhatsApp(template.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-600 line-clamp-4 whitespace-pre-wrap">
                          {template.message}
                        </p>
                      </div>
                      
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>Creada: {new Date(template.created_at).toLocaleDateString('es-ES')}</span>
                        <span>{template.message.length} caracteres</span>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
